import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

function formatCode(raw) {
  const letters = (raw || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8);
  return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}

function when(iso) {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString();
}

/** Desktop apps sign in here: the app shows a code, you approve it. */
export default function LinkApp() {
  const { user, loading, login } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState(formatCode(params.get('code')));
  const [pending, setPending] = useState(null);   // the link awaiting a decision
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [tokens, setTokens] = useState([]);

  const loadTokens = useCallback(async () => {
    const res = await fetch(`${API}/auth/app-tokens`, { headers: headers(), credentials: 'include' });
    if (res.ok) setTokens(await res.json());
  }, []);

  const lookUp = useCallback(async (value) => {
    setError(null); setMessage(null); setPending(null);
    const res = await fetch(`${API}/auth/device/${encodeURIComponent(value)}`, {
      headers: headers(), credentials: 'include'
    });
    if (res.ok) setPending(await res.json());
    else setError((await res.json().catch(() => ({}))).detail || 'that code was not found');
  }, []);

  useEffect(() => {
    if (!user) return;
    loadTokens();
    const initial = formatCode(params.get('code'));
    if (initial.length === 9) lookUp(initial);
  }, [user, params, loadTokens, lookUp]);

  const decide = async (decision) => {
    const res = await fetch(`${API}/auth/device/${pending.user_code}/${decision}`, {
      method: 'POST', headers: headers(), credentials: 'include'
    });
    const body = await res.json().catch(() => ({}));
    setPending(null);
    if (!res.ok) { setError(body.detail || 'that did not work'); return; }
    setCode('');
    setMessage(decision === 'approve'
      ? `${body.client_name} is signed in as ${user.username}. you can close this tab.`
      : `${body.client_name} was refused.`);
    setTimeout(loadTokens, 4000);
  };

  const revoke = async (token) => {
    if (!window.confirm(`sign "${token.name}" out? it will need to be linked again.`)) return;
    const res = await fetch(`${API}/auth/app-tokens/${token.token_id}`, {
      method: 'DELETE', headers: headers(), credentials: 'include'
    });
    if (res.ok) loadTokens();
  };

  // login remembers this page (with its ?code=), so google brings you straight back here
  const signIn = () => login();

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase animate-pulse">loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="link-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-3">link an app</h1>
        <p className="text-[#888888] lowercase mb-10">
          the soma harness signs in to logOS with a code. it never sees your password.
        </p>

        {!user ? (
          <button onClick={signIn}
            className="border border-[#383f38] px-8 py-4 text-white lowercase rounded-xl hover:bg-white hover:text-black transition-all duration-300 font-mono"
            data-testid="link-sign-in">
            sign in to continue
          </button>
        ) : (
          <>
            {!pending && (
              <form onSubmit={(e) => { e.preventDefault(); lookUp(code); }} className="flex gap-3 flex-wrap">
                <input value={code} onChange={(e) => setCode(formatCode(e.target.value))}
                  placeholder="ABCD-EFGH" autoFocus spellCheck={false} autoComplete="off"
                  className="bg-transparent border border-[#383f38] rounded-lg px-4 py-3 text-white font-mono text-2xl tracking-[0.2em] w-64 outline-none focus:border-white transition-colors"
                  data-testid="link-code" />
                <button type="submit" disabled={code.length !== 9}
                  className="border border-[#383f38] px-6 py-3 text-white lowercase rounded-lg hover:bg-white hover:text-black transition-all duration-300 font-mono disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-white">
                  continue
                </button>
              </form>
            )}

            {pending && (
              <div className="border border-[#383f38] rounded-xl p-6 space-y-5" data-testid="link-confirm">
                <p className="text-white lowercase text-lg">
                  sign <span className="font-mono">{pending.client_name}</span> in as <span className="font-mono">{user.username}</span>?
                </p>
                <p className="text-[#888888] lowercase text-sm">
                  code <span className="font-mono text-white normal-case">{pending.user_code}</span>, requested {when(pending.created_at)}.
                  only approve a code your own app is showing you right now. the app will be able to do what you can do on logOS
                  {user.role === 'admin' ? ', including admin actions' : ''}.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => decide('approve')}
                    className="border border-white px-6 py-2 text-black bg-white lowercase rounded-lg hover:bg-transparent hover:text-white transition-all duration-300 font-mono"
                    data-testid="link-approve">
                    approve
                  </button>
                  <button onClick={() => decide('deny')}
                    className="border border-[#383f38] px-6 py-2 text-[#888888] lowercase rounded-lg hover:text-white hover:border-[#888888] transition-all duration-300 font-mono"
                    data-testid="link-deny">
                    deny
                  </button>
                </div>
              </div>
            )}

            {message && <p className="mt-6 text-white lowercase" data-testid="link-message">{message}</p>}
            {error && <p className="mt-6 text-[#cc6666] lowercase" data-testid="link-error">{error}</p>}

            <div className="mt-16 pt-8 border-t border-[#1e251f]">
              <p className="text-[#888888] lowercase text-sm mb-4">linked apps</p>
              {tokens.length === 0 ? (
                <p className="text-[#555555] lowercase text-sm">none yet</p>
              ) : (
                <div className="space-y-2 font-mono text-sm">
                  {tokens.map(t => (
                    <div key={t.token_id} className="flex items-center gap-4 flex-wrap text-[#aaaaaa]" data-testid="app-token-row">
                      <span className="text-white">{t.name}</span>
                      <span className="text-[#555555]">linked {when(t.created_at)}</span>
                      <span className="text-[#555555]">last used {when(t.last_used_at)}</span>
                      <button onClick={() => revoke(t)}
                        className="lowercase text-[#555555] hover:text-white transition-colors">
                        sign out
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
